// LinkedIn Integration Registration
// Registers LinkedIn integration with the integration registry

import { IntegrationRegistry } from '../registry/IntegrationRegistry';
import { LinkedInIntegrationFactory } from './LinkedInIntegrationFactory';
import { IntegrationConfig } from '../../../types/integration';

/**
 * Register LinkedIn integration with the integration registry
 */
export function registerLinkedInIntegration(): void {
  const registry = IntegrationRegistry.getInstance();
  
  // Create a factory that implements the IntegrationFactory interface
  const linkedInFactory = {
    async create(config: IntegrationConfig) {
      return await LinkedInIntegrationFactory.create(config as any);
    },
    
    async validate(config: Partial<IntegrationConfig>) {
      if (!config.credentials) {
        return {
          valid: false,
          errors: [{ field: 'credentials', message: 'Credentials are required', code: 'REQUIRED' }],
          warnings: []
        };
      }
      
      try {
        const credentials = JSON.parse((config.credentials as any).encrypted);
        const testResult = await LinkedInIntegrationFactory.testCredentials(credentials);
        
        if (testResult.success) {
          return {
            valid: true,
            errors: [],
            warnings: []
          };
        } else {
          return {
            valid: false,
            errors: [{ field: 'credentials', message: testResult.message, code: 'INVALID' }],
            warnings: []
          };
        }
      } catch (error) {
        return {
          valid: false,
          errors: [{ field: 'credentials', message: 'Invalid credentials format', code: 'INVALID' }],
          warnings: []
        };
      }
    },
    
    getConfigSchema() {
      return {
        credentials: {
          accessToken: {
            type: 'string',
            required: true,
            description: 'LinkedIn OAuth 2.0 access token',
            sensitive: true
          },
          clientId: {
            type: 'string',
            required: true,
            description: 'LinkedIn application client ID'
          },
          clientSecret: {
            type: 'string',
            required: true,
            description: 'LinkedIn application client secret',
            sensitive: true
          },
          organizationId: {
            type: 'string',
            required: false,
            description: 'Specific LinkedIn organization ID (optional)'
          }
        },
        settings: {
          enableOrganicPosting: {
            type: 'boolean',
            default: true,
            description: 'Enable organic post publishing'
          },
          enableAnalytics: {
            type: 'boolean',
            default: true,
            description: 'Enable analytics and engagement tracking'
          },
          defaultOrganization: {
            type: 'string',
            required: false,
            description: 'Default organization for posting'
          },
          contentSettings: {
            type: 'object',
            properties: {
              maxHashtags: {
                type: 'number',
                default: 5,
                description: 'Maximum number of hashtags per post'
              },
              autoAddHashtags: {
                type: 'boolean',
                default: false,
                description: 'Automatically add relevant hashtags'
              },
              mentionNotifications: {
                type: 'boolean',
                default: true,
                description: 'Enable mention notifications'
              }
            }
          }
        }
      };
    },
    
    getSupportedFeatures() {
      return [
        'post_publishing',
        'analytics',
        'organization_management',
        'engagement_tracking'
      ];
    }
  };

  // Register the integration
  registry.register(
    'social:linkedin',
    'LinkedIn Marketing',
    'social',
    'linkedin',
    '1.0.0',
    linkedInFactory
  );

  console.log('LinkedIn integration registered successfully');
}